<script>
import { required } from "@vuelidate/validators";

import AlerteConfirmationMagasinChargement from "./chargement";
import Button from "@/components/atoms/button";
import ButtonGroup from "@/components/molecules/button-group";
import Icon from "@/components/atoms/icon";
import Alert from "@/components/molecules/alert";
import Divider from "@/components/atoms/divider";
import Form from "@/components/molecules/form";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import FormSelectOptGroup from "@/components/molecules/form-select/form-select-optgroup";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";

export default {
  name: "AlerteConfirmationMagasin",
  components: {
    Button,
    ButtonGroup,
    Icon,
    Alert,
    Divider,
    Form,
    FormSelect,
    FormSelectOption,
    FormSelectOptGroup,
    AlerteConfirmationMagasinChargement
  },
  props: {
    magasinName: {
      type: String,
      required: false,
      default: null
    },
    magasinId: {
      type: [Number, String],
      required: false,
      default: null
    },
    magasins: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    isPremiereCommande: {
      type: Boolean,
      required: false,
      default: false
    },
    isMagasinInhabituel: {
      type: Boolean,
      required: false,
      default: false
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    magasinsNearBy: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    ...formValidationProps()
  },
  emits: ["changeMagasin", "confirmeMagasin"],
  setup(props) {
    const { v$, checkValidation } = useFormValidation(props);
    return {
      v$,
      checkValidation
    };
  },
  computed: {
    otherMagasins() {
      const magasins = this.magasins.filter((magasin) => {
        return !this.magasinsNearBy.some((mag) => mag.id === magasin.id);
      });
      if (!this.magasinId) {
        return magasins;
      }
      return magasins.filter((magasin) => {
        return parseInt(magasin.id) !== this.magasinId;
      });
    },
    currentMagasin() {
      return this.magasins.find((magasin) => {
        return parseInt(magasin.id) === this.magasinId;
      });
    }
  },
  data() {
    return {
      changementMagasin: false,
      idMagasin: null,
      nameMagasin: ""
    };
  },
  methods: {
    changeMagasin() {
      const magasin = this.magasins.find((mag) => mag.id === this.idMagasin);
      this.nameMagasin = magasin ? magasin.attributes.magasin : "";

      if (this.checkValidation(this.v$)) {
        this.$emit("changeMagasin", this.idMagasin);
      }
    }
  },
  validations() {
    return {
      idMagasin: { required }
    };
  }
};
</script>

<template>
  <div>
    <AlerteConfirmationMagasinChargement
      v-if="isLoading"
      :magasin-name="nameMagasin"
    />
    <template v-else>
      <template v-if="isPremiereCommande">
        <h2 class="u-font-secondary u-font-24 u-font-700 u-color-primary u-text-center u-mb-xs">
          Pas si vite ! <br> C'est votre première commande sur Cora.fr
        </h2>
        <p class="u-font-15 u-text-center">
          Êtes-vous sûr d’avoir sélectionné le bon magasin ?
        </p>

        <p class="u-flex u-flex-justify-center u-flex-align-center u-mt-md u-mb-lg u-font-21">
          <Icon
            name="location"
            size="sm"
            class="u-mr-xxs"
          />
          Magasin {{ magasinName }}
        </p>
      </template>
      <template v-if="isMagasinInhabituel">
        <h2 class="u-font-secondary u-font-24 u-font-700 u-color-primary u-text-center u-mb-sm">
          Vous avez sélectionné le magasin <br>
          {{ magasinName }}
        </h2>
        <p class="u-font-15 u-text-center u-mb-xlg u-mb-lg@sm">
          Vous n'avez jamais commandé dans ce magasin. <br>
          Souhaitez-vous continuer vos achats dans ce magasin ?
        </p>
      </template>

      <ButtonGroup
        v-if="!changementMagasin"
        class="u-mr-lg u-ml-lg u-mt-md u-mb-lg u-flex-column@sm"
      >
        <Button
          outline
          variant="primary"
          radius="full"
          size="md"
          class="u-mb-sm@sm"
          @click="changementMagasin = !changementMagasin"
        >
          Je change de magasin
        </Button>
        <Button
          variant="secondary"
          radius="full"
          size="md"
          @click="$emit('confirmeMagasin')"
        >
          Je confirme ce magasin
        </Button>
      </ButtonGroup>

      <div
        v-if="changementMagasin"
        class="u-mr-lg u-ml-lg u-mt-md u-mb-lg"
      >
        <Divider dotted />
        <p class="u-font-secondary u-font-24 u-font-700 u-color-primary u-text-center u-mb-sm u-mt-md">
          Changer de magasin
        </p>
        <p class="u-font-15 u-text-center">
          Sélectionnez votre nouveau magasin dans la liste ci-dessous
        </p>

        <Form @submit.prevent="changeMagasin">
          <div class="u-flex u-flex-justify-center u-mt-md">
            <FormSelect
              v-model="idMagasin"
              title="Magasin"
              default-value="Sélectionnez un magasin"
              variant="primary"
            >
              <FormSelectOptGroup
                v-if="currentMagasin"
                label="Vous êtes rattaché(e) au magasin"
              >
                <FormSelectOption
                  :value="currentMagasin.id"
                  disabled
                >
                  {{ currentMagasin.attributes.magasin }}
                </FormSelectOption>
              </FormSelectOptGroup>
              <FormSelectOptGroup
                v-if="magasinsNearBy.length > 0"
                label="Les magasins proche de chez vous"
              >
                <FormSelectOption
                  v-for="magasin in magasinsNearBy"
                  :key="magasin.id"
                  :value="magasin.id"
                >
                  {{ magasin.attributes.magasin }}
                </FormSelectOption>
              </FormSelectOptGroup>
              <FormSelectOptGroup label="Autres magasins">
                <FormSelectOption
                  v-for="magasin in otherMagasins"
                  :key="magasin.id"
                  :value="magasin.id"
                >
                  {{ magasin.attributes.magasin }}
                </FormSelectOption>
              </FormSelectOptGroup>
            </FormSelect>
          </div>

          <div class="u-mr-lg u-ml-sm u-mt-md u-mb-lg u-text-center">
            <Button
              variant="secondary"
              radius="full"
              size="md"
              :disabled="!idMagasin"
              type="submit"
              value="Je confirme ce magasin"
            />
          </div>
        </Form>
      </div>

      <Alert
        has-icon
        icon-position="left"
        icon-name="info-circle"
        icon-size="sm"
        icon-color="primary-light"
        radius="md"
        class="u-ml-sm u-mr-sm u-ml-xxs@sm u-mr-xxs@sm"
      >
        Si vous changez de magasin, votre panier ne sera pas conservé.<br />
        Il sera enregistré dans la liste de courses « Panier sauvegardé »
      </Alert>
    </template>
  </div>
</template>
